// 用電 › 用電排行（plan SPEC NRG-V3；設計稿 v02 / v02b / v02c / v02d / v02e）：
//   名稱（顯示名 + 地點路徑）、種類、本期 kWh、平均功率、最高功率、估算電費（每列自己的幣別）、比上期、功耗模式；
//   預設「依設備」（PoE 埠歸到接著的攝影機），可切「依 PoE 孔」（依 Station 分組）；排序、篩種類 / {leaf}、搜尋；
//   勾選攝影機〔設定功耗模式…〕（第 2 級確認，UX-03；逐台 device.control，沒權限的勾選框停用並寫原因）→ 逐台結果 pending → applied / failed；
//   每列點進設備頁對應位置；〔匯出 CSV〕；手機一列一張卡、排序與篩選收進底部抽屜。
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useScope } from '../../lib/scope';
import { useViewportWidth } from '../../lib/useViewport';
import { Dialog } from '../../components/Dialog';
import { energyApi, energyMode } from './api';
import { approxValue, hhmm, localParts, NBSP, type Fmt } from './format';
import { useEnergyEnv, useEnergyTargets, useFmt } from './hooks';
import { csvFileName, csvKwh, csvMoney, downloadText, meterName, toCsv, type EnergyTarget } from './model';
import { Cmp, energyErrText, LoadError } from './parts';
import type { MeterKind, PowerModeResult, RankRow } from './types';

type SortKey = 'mwh' | 'avg' | 'max' | 'cmp';
const PAGE = 100;

function prevMonthDate(): string {
  const d = new Date();
  const p = new Date(d.getFullYear(), d.getMonth() - 1, 1);
  return `${p.getFullYear()}-${String(p.getMonth() + 1).padStart(2, '0')}-01`;
}

function rowPath(r: RankRow): string {
  const h = encodeURIComponent(r.home_id);
  if (r.device_ref) return `/devices/${h}/${encodeURIComponent(r.device_ref)}?tab=overview#energy`;
  if (r.station_ref) return `/devices/${h}/${encodeURIComponent(r.station_ref)}?tab=channels&port=${r.port ?? ''}#site`;
  return '';
}

export function Ranking() {
  const { t } = useTranslation();
  useEnergyEnv();
  const fmt = useFmt();
  const targets = useEnergyTargets();
  const { visible } = useScope();
  const [sp, setSp] = useSearchParams();
  const isMobile = useViewportWidth() <= 800;
  const gaps = sp.get('gaps') === '1';
  const [period, setPeriod] = useState<'month' | 'last'>('month');
  const [group, setGroup] = useState<'device' | 'poe_port'>('device');
  const [kind, setKind] = useState<MeterKind | ''>(gaps ? '' : '');
  const [home, setHome] = useState('');
  const [text, setText] = useState('');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<SortKey>('mwh');
  const [page, setPage] = useState(0);
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [sheet, setSheet] = useState(false);
  const [confirm, setConfirm] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setQ(text.trim()), 300);
    return () => clearTimeout(id);
  }, [text]);
  useEffect(() => {
    setPage(0);
    setCursors([undefined]);
  }, [period, group, kind, home, q, sort, targets.length]);
  const date = period === 'last' ? prevMonthDate() : undefined;
  const multi = targets.length > 1;
  const args = (tg: EnergyTarget) => ({
    org: tg.org,
    scope: home ? 'home:' + home : tg.scope,
    period: 'month' as const,
    date,
    kind: kind || undefined,
    group,
    sort: `${sort}:desc`,
    cursor: multi ? undefined : cursors[page],
    text: q || undefined,
    limit: PAGE,
    ...(gaps ? { gaps: '1' } : {}),
  });
  const qs = useQueries({
    queries: targets
      .filter((tg) => !home || visible.find((h) => h.home_id === home)?.org_id === tg.org)
      .map((tg) => ({
        queryKey: ['energy', 'ranking', energyMode(), tg.api, tg.org, JSON.stringify(args(tg))],
        queryFn: () => energyApi.ranking(tg.api, args(tg)),
        staleTime: 60_000,
        retry: false,
      })),
  });
  const loading = qs.some((x) => x.isLoading);
  const err = qs.find((x) => x.isError)?.error;
  const names = useMemo(() => Object.assign({}, ...qs.map((x) => x.data?.names?.homes ?? {})) as Record<string, { name: string; path?: string }>, [qs.map((x) => x.dataUpdatedAt).join()]); // eslint-disable-line react-hooks/exhaustive-deps
  let rows: RankRow[] = qs.flatMap((x) => x.data?.items ?? []);
  if (gaps) rows = rows.filter((r) => r.incomplete_hours > 0 || r.unavailable);
  // 多個組織合併、或平台不支援的排序鍵（平均、比上期）：這一頁在畫面上排
  if (multi || sort === 'avg' || sort === 'cmp') rows = [...rows].sort((a, b) => val(b, sort) - val(a, sort)).slice(0, PAGE);
  const total = qs.reduce((a, x) => a + (x.data?.total ?? 0), 0);
  const next = !multi ? qs[0]?.data?.next_cursor : null;
  const pickable = rows.filter((r) => r.power_mode !== undefined && r.can_control && r.device_ref);
  const hasCams = rows.some((r) => r.power_mode !== undefined);
  const selRows = rows.filter((r) => sel.has(r.meter));
  const homeOf = (id: string) => names[id]?.name ?? visible.find((h) => h.home_id === id)?.display_name ?? id;

  const exportCsv = async () => {
    // 全部頁（逐組織、每頁 100；最多 50 頁）
    const all: RankRow[] = [];
    for (const tg of targets) {
      let cursor: string | undefined;
      for (let i = 0; i < 50; i++) {
        const r = await energyApi.ranking(tg.api, { ...args(tg), cursor });
        all.push(...r.items);
        if (!r.next_cursor) break;
        cursor = r.next_cursor;
      }
    }
    const curs = [...new Set(all.flatMap((r) => r.costs.map((c) => c.currency)))];
    const csv = toCsv(
      t('energy:csv.disclaimer'),
      [t('energy:csv.home'), t('energy:csv.meter_name'), t('energy:csv.meter_kind'), t('energy:csv.kwh'), t('energy:csv.avg_w'), t('energy:csv.max_w'), ...curs.map((c) => t('energy:csv.cost', { cur: c })), t('energy:csv.quality'), t('energy:csv.missing_h')],
      all.map((r) => [
        homeOf(r.home_id),
        meterName(t, r, { crossSite: true, homeName: homeOf(r.home_id) }).name,
        t(`energy:kind.${r.kind}`),
        csvKwh(r.mwh),
        r.avg_mw === null ? '' : (r.avg_mw / 1000).toFixed(1),
        r.max_mw === null ? '' : (r.max_mw / 1000).toFixed(1),
        ...curs.map((c) => csvMoney(r.costs.find((x) => x.currency === c))),
        t(`energy:q.${r.unavailable ? 'incomplete' : r.quality}`),
        r.incomplete_hours,
      ]),
    );
    const now = new Date();
    downloadText(csvFileName(t('energy:rank.csv_name'), visible.length === 1 ? (visible[0].display_name ?? '') : t('energy:rank.all_homes'), date ? date.slice(0, 7) : `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`), csv);
  };

  const filters = (
    <>
      <div className="km-seg" role="group" aria-label={t('energy:report.month')}>
        <button type="button" aria-pressed={period === 'month'} onClick={() => setPeriod('month')}>
          {t('energy:period.month_to_date')}
        </button>
        <button type="button" aria-pressed={period === 'last'} onClick={() => setPeriod('last')}>
          {t('energy:period.last_month')}
        </button>
      </div>
      <div className="km-seg" role="group" aria-label={t('energy:rank.group')}>
        <button type="button" aria-pressed={group === 'device'} onClick={() => setGroup('device')} data-testid="en-group-device">
          {t('energy:rank.by_device')}
        </button>
        <button type="button" aria-pressed={group === 'poe_port'} onClick={() => setGroup('poe_port')} data-testid="en-group-port">
          {t('energy:rank.by_port')}
        </button>
      </div>
      <span role="group" aria-label={t('energy:rank.kinds')} className="en-row" style={{ gap: 6 }}>
        {(['plug', 'poe', 'meter'] as MeterKind[]).map((k) => (
          <button key={k} type="button" className={`km-chip${kind === k ? ' on' : ''}`} aria-pressed={kind === k} onClick={() => setKind(kind === k ? '' : k)} data-testid={`en-kind-${k}`}>
            {t(`energy:kind.${k}`)}
          </button>
        ))}
      </span>
      {visible.length > 1 && (
        <select className="input" value={home} onChange={(e) => setHome(e.target.value)} aria-label={t('energy:rank.all_homes')} data-testid="en-home-filter">
          <option value="">{t('energy:rank.all_homes')}</option>
          {visible
            .filter((h) => !h.external)
            .map((h) => (
              <option key={h.home_id} value={h.home_id}>
                {h.display_name ?? h.home_id}
              </option>
            ))}
        </select>
      )}
      <input className="input" type="search" value={text} onChange={(e) => setText(e.target.value)} placeholder={t('energy:rank.search')} aria-label={t('energy:rank.search')} data-testid="en-rank-search" />
    </>
  );

  return (
    <div data-testid="en-ranking">
      <div className="en-row" style={{ marginBottom: 12 }}>
        {isMobile ? (
          <button type="button" className="btn" onClick={() => setSheet(true)} data-testid="en-filters-btn">
            {t('energy:rank.filters')}
          </button>
        ) : (
          filters
        )}
        <span className="spacer" />
        <button type="button" className="btn" onClick={() => void exportCsv()} data-testid="en-rank-csv">
          <span aria-hidden>⤓</span> {t('energy:rank.export')}
        </button>
      </div>
      {gaps && (
        <div className="km-bar" role="status">
          <span className="ic" aria-hidden>
            ⊘
          </span>
          <span className="txt small">{t('energy:card.incomplete', { meters: rows.length, hours: rows.reduce((a, r) => a + r.incomplete_hours, 0) })}</span>
          <button type="button" className="btn btn-sm" onClick={() => (sp.delete('gaps'), setSp(sp))}>
            {t('energy:apply.clear_filter')}
          </button>
        </div>
      )}
      {err && !rows.length ? (
        <LoadError error={err} retry={() => qs.forEach((x) => void x.refetch())} />
      ) : loading && !rows.length ? (
        <section className="card" aria-busy="true">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="skel" />
          ))}
        </section>
      ) : !rows.length ? (
        <section className="card">
          <p className="muted" data-testid="en-rank-empty">
            {t('energy:rank.empty')}
          </p>
        </section>
      ) : (
        <section className="card" style={{ padding: 0 }}>
          {isMobile ? (
            <MobileRows rows={rows} fmt={fmt} homeOf={homeOf} sel={sel} setSel={setSel} />
          ) : (
            <RankTable rows={rows} fmt={fmt} group={group} sort={sort} setSort={setSort} homeOf={homeOf} sel={sel} setSel={setSel} hasCams={hasCams} pickable={pickable} />
          )}
          <p className="en-note" style={{ padding: '8px 16px' }} data-testid="en-rank-footer">
            {kind || q
              ? t('energy:rank.footer_filtered', { f: kind ? t(`energy:kind.${kind}`) : q, from: page * PAGE + 1, to: page * PAGE + rows.length, total })
              : t('energy:rank.footer', { from: page * PAGE + 1, to: page * PAGE + rows.length, total })}
            {page > 0 && (
              <>
                {' · '}
                <button type="button" className="link-btn" onClick={() => setPage((p) => p - 1)}>
                  {t('energy:rank.prev')}
                </button>
              </>
            )}
            {next && (
              <>
                {' · '}
                <button
                  type="button"
                  className="link-btn"
                  onClick={() => {
                    setCursors((c) => [...c.slice(0, page + 1), next]);
                    setPage((p) => p + 1);
                  }}
                >
                  {t('energy:rank.next')}
                </button>
              </>
            )}
            {' · '}
            {t('energy:card.cost_disclaimer')}
          </p>
        </section>
      )}
      {selRows.length > 0 && (
        <div className="en-batch" role="region" aria-label={t('energy:pm.picked', { n: selRows.length })} data-testid="en-batch">
          <b>{t('energy:pm.picked', { n: selRows.length })}</b>
          <span className="why">{t('energy:pm.why')}</span>
          <button type="button" className="btn" onClick={() => setSel(new Set())}>
            {t('energy:pm.clear')}
          </button>
          <button type="button" className="btn btn-primary" onClick={() => setConfirm(true)} data-testid="en-pm-open">
            {t('energy:pm.set')}
          </button>
        </div>
      )}
      {confirm && <PowerModeFlow rows={selRows} fmt={fmt} targets={targets} close={() => setConfirm(false)} done={() => setSel(new Set())} />}
      {sheet && (
        <Dialog open title={t('energy:rank.filters')} onClose={() => setSheet(false)} drawer testId="en-filter-sheet">
          <div style={{ display: 'grid', gap: 12 }}>
            {filters}
            <label className="field-label">
              {t('energy:rank.sort')}
              <select className="input" value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
                <option value="mwh">{t('energy:rank.sort_kwh')}</option>
                <option value="avg">{t('energy:rank.sort_avg')}</option>
                <option value="max">{t('energy:rank.sort_max')}</option>
                <option value="cmp">{t('energy:rank.sort_cmp')}</option>
              </select>
            </label>
            <button type="button" className="btn btn-primary" onClick={() => setSheet(false)}>
              {t('energy:rank.show', { n: total })}
            </button>
          </div>
        </Dialog>
      )}
    </div>
  );
}

function val(r: RankRow, k: SortKey): number {
  return k === 'avg' ? (r.avg_mw ?? -1) : k === 'max' ? (r.max_mw ?? -1) : k === 'cmp' ? (r.comparison?.ratio ?? -Infinity) : r.mwh;
}

function modeNote(t: (k: string, o?: Record<string, unknown>) => string, fmt: Fmt, r: RankRow): string | null {
  if (!r.power_mode_changed_at || r.power_mode === undefined) return null;
  const lp = localParts(r.power_mode_changed_at);
  const date = `${lp.m}/${lp.d}`;
  const mode = t(`energy:pm.${r.power_mode}`);
  if (!r.power_mode_effect) return t('energy:rank.mode_changed', { date, mode });
  const c = fmt.compare((r.power_mode_effect.after_mwh - r.power_mode_effect.before_mwh) / r.power_mode_effect.before_mwh, r.power_mode_effect.before_mwh, r.power_mode_effect.after_mwh);
  return t('energy:rank.mode_effect', { date, mode, days: r.power_mode_effect.days, cmp: c.aria });
}

function NameCell({ r, fmt, homeOf, crossSite }: { r: RankRow; fmt: Fmt; homeOf: (id: string) => string; crossSite: boolean }) {
  const { t } = useTranslation();
  const m = meterName(t, r, { crossSite, homeName: homeOf(r.home_id) });
  const path = rowPath(r);
  const note = modeNote(t, fmt, r);
  return (
    <>
      {path ? (
        <Link to={path} style={{ fontWeight: 600 }}>
          {m.name}
        </Link>
      ) : (
        <b>{m.name}</b>
      )}
      <div className="en-note">{[homeOf(r.home_id), m.sub].filter(Boolean).join(' · ')}</div>
      {note && <div className="en-note">{note}</div>}
      {r.power_mode !== undefined && !r.can_control && <div className="en-note">{t('energy:rank.no_control')}</div>}
    </>
  );
}

function RankTable({
  rows,
  fmt,
  group,
  sort,
  setSort,
  homeOf,
  sel,
  setSel,
  hasCams,
  pickable,
}: {
  rows: RankRow[];
  fmt: Fmt;
  group: 'device' | 'poe_port';
  sort: SortKey;
  setSort: (k: SortKey) => void;
  homeOf: (id: string) => string;
  sel: Set<string>;
  setSel: (s: Set<string>) => void;
  hasCams: boolean;
  pickable: RankRow[];
}) {
  const { t } = useTranslation();
  const th = (k: SortKey, label: string) => (
    <th scope="col" className="num" aria-sort={sort === k ? 'descending' : undefined}>
      <button type="button" className="sort" onClick={() => setSort(k)}>
        {label}
        {sort === k && <span aria-hidden> ▼</span>}
      </button>
    </th>
  );
  const allOn = pickable.length > 0 && pickable.every((r) => sel.has(r.meter));
  const toggle = (r: RankRow) => {
    const s = new Set(sel);
    if (s.has(r.meter)) s.delete(r.meter);
    else s.add(r.meter);
    setSel(s);
  };
  // 依 PoE 孔：依 Station 分組、組內照孔號
  const groups: { key: string; title: string; rows: RankRow[] }[] = [];
  if (group === 'poe_port') {
    for (const r of rows) {
      const k = `${r.home_id}|${r.station_ref ?? ''}`;
      let g = groups.find((x) => x.key === k);
      if (!g) groups.push((g = { key: k, title: t('energy:rank.by_station', { home: homeOf(r.home_id), station: r.station_name ?? '' }), rows: [] }));
      g.rows.push(r);
    }
    for (const g of groups) g.rows.sort((a, b) => (a.port ?? 0) - (b.port ?? 0));
  } else groups.push({ key: '', title: '', rows });
  const cols = 8 + (hasCams ? 2 : 0);
  return (
    <div className="table-wrap">
      <table className="km-table" data-testid="en-rank-table">
        <thead>
          <tr>
            {hasCams && (
              <th scope="col" style={{ width: 44 }}>
                <input
                  type="checkbox"
                  aria-label={t('energy:rank.pick_all')}
                  checked={allOn}
                  disabled={!pickable.length}
                  onChange={() => setSel(allOn ? new Set() : new Set(pickable.map((r) => r.meter)))}
                />
              </th>
            )}
            <th scope="col">{t('energy:rank.col_name')}</th>
            <th scope="col">{t('energy:rank.col_kind')}</th>
            {th('mwh', t('energy:rank.col_kwh'))}
            {th('avg', t('energy:rank.col_avg'))}
            {th('max', t('energy:rank.col_max'))}
            <th scope="col" className="num">
              {t('energy:rank.col_cost')}
            </th>
            {th('cmp', t('energy:rank.col_cmp'))}
            {hasCams && <th scope="col">{t('energy:rank.col_mode')}</th>}
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => (
            <GroupRows key={g.key} g={g} cols={cols}>
              {g.rows.map((r) => (
                <tr key={r.meter} data-meter={r.meter} className={sel.has(r.meter) ? 'sel' : undefined}>
                  {hasCams && (
                    <td>
                      {r.power_mode === undefined ? null : r.can_control ? (
                        <input type="checkbox" aria-label={t('energy:rank.pick_row', { name: r.name })} checked={sel.has(r.meter)} onChange={() => toggle(r)} />
                      ) : (
                        <span className="en-lockcell" title={t('energy:rank.no_control')} aria-label={t('energy:rank.no_control')}>
                          🔒
                        </span>
                      )}
                    </td>
                  )}
                  <td>
                    <NameCell r={r} fmt={fmt} homeOf={homeOf} crossSite={group === 'device'} />
                  </td>
                  <td>{t(`energy:kind.${r.kind}`)}</td>
                  <td className="r tabular">{r.unavailable ? t('energy:table.unavailable') : fmt.energyNum(r.mwh)}</td>
                  <td className="r tabular">{fmt.power(r.avg_mw)}</td>
                  <td className="r tabular">{fmt.power(r.max_mw)}</td>
                  <td className="r tabular">{r.costs.length ? r.costs.map((m) => fmt.money(m)).join(' · ') : <span className="muted">{t('energy:table.no_tariff')}</span>}</td>
                  <td className="r">
                    <Cmp fmt={fmt} cmp={r.comparison} cur={r.mwh} reason={r.no_compare_reason} />
                  </td>
                  {hasCams && <td>{r.power_mode === undefined ? '' : t(`energy:pm.${r.power_mode}`)}</td>}
                </tr>
              ))}
            </GroupRows>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GroupRows({ g, cols, children }: { g: { key: string; title: string }; cols: number; children: React.ReactNode }) {
  return (
    <>
      {g.title && (
        <tr className="en-port-open">
          <th scope="rowgroup" colSpan={cols} style={{ textAlign: 'left' }}>
            {g.title}
          </th>
        </tr>
      )}
      {children}
    </>
  );
}

function MobileRows({ rows, fmt, homeOf, sel, setSel }: { rows: RankRow[]; fmt: Fmt; homeOf: (id: string) => string; sel: Set<string>; setSel: (s: Set<string>) => void }) {
  const { t } = useTranslation();
  return (
    <ul className="en-cardlist" style={{ padding: '0 16px' }} data-testid="en-rank-cards">
      {rows.map((r) => {
        const m = meterName(t, r, { crossSite: true, homeName: homeOf(r.home_id) });
        const cmp = r.comparison ? fmt.compare(r.comparison.ratio, r.comparison.prev_mwh, r.mwh).text : '—';
        const path = rowPath(r);
        return (
          <li key={r.meter} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            {r.power_mode !== undefined && r.can_control && (
              <input
                type="checkbox"
                aria-label={t('energy:rank.pick_row', { name: r.name })}
                checked={sel.has(r.meter)}
                onChange={() => {
                  const s = new Set(sel);
                  if (s.has(r.meter)) s.delete(r.meter);
                  else s.add(r.meter);
                  setSel(s);
                }}
              />
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="l1">
                {path ? <Link to={path}>{m.name}</Link> : <b>{m.name}</b>}
                <span className="tabular">{fmt.energy(r.mwh)}</span>
              </div>
              <div className="l2">
                {[homeOf(r.home_id), t(`energy:kind.${r.kind}`)].join(' · ')}
                <br />
                {[cmp, r.costs.map((c) => fmt.money(c)).join(' · '), r.power_mode !== undefined ? t(`energy:pm.${r.power_mode}`) : ''].filter(Boolean).join(' · ')}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

// ---- 功耗模式：第 2 級確認 → 逐台結果 ----

function PowerModeFlow({ rows, fmt, targets, close, done }: { rows: RankRow[]; fmt: Fmt; targets: EnergyTarget[]; close: () => void; done: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [mode, setMode] = useState<number>(2);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [results, setResults] = useState<PowerModeResult[] | null>(null);
  const byOrg = useMemo(() => {
    const m = new Map<string, RankRow[]>();
    for (const r of rows) m.set(r.org_id ?? targets[0]?.org ?? '', [...(m.get(r.org_id ?? targets[0]?.org ?? '') ?? []), r]);
    return m;
  }, [rows, targets]);
  const apiOf = (org: string) => targets.find((x) => x.org === org)?.api ?? targets[0]?.api ?? '';
  // 省多少：依最近 7 天推估（省電優先約少 20%，2 位有效數字）
  const saving = mode === 2 ? approxValue((rows.reduce((a, r) => a + r.mwh, 0) / 1e6) * 0.2) : 0;
  const run = async (only?: string[]) => {
    setBusy(true);
    setErr(null);
    try {
      const out: PowerModeResult[] = [];
      for (const [org, rs] of byOrg) {
        const refs = rs.map((r) => r.device_ref!).filter((x) => !only || only.includes(x));
        if (!refs.length) continue;
        const r = await energyApi.powerMode(apiOf(org), org, refs, mode);
        out.push(...r.results);
      }
      setResults((prev) => (prev && only ? prev.map((p) => out.find((o) => o.device_ref === p.device_ref) ?? p) : out));
      void qc.invalidateQueries({ queryKey: ['energy', 'ranking'] });
    } catch (e) {
      setErr(t('energy:pm.err', { msg: energyErrText(t, e) }));
    } finally {
      setBusy(false);
    }
  };
  // 等待中的每 2 秒問一次狀態，直到沒有 pending 或過了期限
  useEffect(() => {
    if (!results?.some((r) => r.status === 'pending')) return;
    const id = setInterval(async () => {
      for (const [org, rs] of byOrg) {
        const pend = results.filter((r) => r.status === 'pending' && rs.some((x) => x.device_ref === r.device_ref)).map((r) => r.device_ref);
        if (!pend.length) continue;
        try {
          const st = await energyApi.powerModeStatus(apiOf(org), org, pend);
          setResults((prev) =>
            (prev ?? []).map((p) => {
              const s = st.results.find((x) => x.device_ref === p.device_ref);
              if (!s) return p;
              // 過了期限還沒套用 → 設備沒有套用
              if (s.status === 'pending' && p.deadline && Date.now() > Date.parse(p.deadline)) return { ...p, status: 'failed' };
              return { ...p, status: s.status };
            }),
          );
          void qc.invalidateQueries({ queryKey: ['energy', 'ranking'] });
        } catch {
          /* 下一輪再問 */
        }
      }
    }, 2000);
    return () => clearInterval(id);
  }, [results, byOrg]); // eslint-disable-line react-hooks/exhaustive-deps
  const nameOf = (ref: string) => rows.find((r) => r.device_ref === ref)?.name ?? ref;
  if (results) {
    const applied = results.filter((r) => r.status === 'applied').length;
    const pending = results.filter((r) => r.status === 'pending').length;
    const failed = results.filter((r) => r.status === 'failed');
    const deadline = results.map((r) => r.deadline).filter(Boolean).sort()[0];
    return (
      <Dialog open title={t('energy:pm.result_title')} onClose={() => (done(), close())} testId="en-pm-result">
        <div className="km-bar" role="status" data-testid="en-pm-summary">
          <span className="ic" aria-hidden>
            ⓘ
          </span>
          <span className="txt">
            {t('energy:pm.result_summary', { applied, pending })}
            {failed.length > 0 && <> · {t('energy:pm.result_failed', { n: failed.length })}</>}
          </span>
        </div>
        <ul className="plain-list">
          {results.map((r) => (
            <li key={r.device_ref} data-status={r.status} style={{ padding: '6px 0', borderTop: '1px solid var(--border)' }}>
              <b>{nameOf(r.device_ref)}</b>{NBSP}
              <span className={r.status === 'failed' ? 'en-err' : r.status === 'applied' ? '' : 'muted'}>
                {r.status === 'failed' && r.error === 'FORBIDDEN' ? t('energy:pm.st_forbidden') : t(`energy:pm.st_${r.status}`)}
              </span>
              {r.status === 'failed' && r.error !== 'FORBIDDEN' && (
                <button type="button" className="btn btn-sm" style={{ marginLeft: 8 }} disabled={busy} onClick={() => void run([r.device_ref])}>
                  {t('energy:pm.retry')}
                </button>
              )}
            </li>
          ))}
        </ul>
        {pending > 0 && deadline && <p className="en-note">{t('energy:pm.deadline', { time: hhmm(deadline) })}</p>}
        <div className="dialog-actions">
          <button type="button" className="btn btn-primary" onClick={() => (done(), close())}>
            {t('energy:pm.done')}
          </button>
        </div>
      </Dialog>
    );
  }
  return (
    <Dialog open title={t('energy:pm.dlg_title', { n: rows.length })} onClose={close} testId="en-pm-confirm">
      <div data-testid="confirm-dialog" data-level={2}>
        <fieldset className="km-check" style={{ border: 0, padding: 0, display: 'grid', gap: 6 }}>
          <legend className="small muted">{t('energy:rank.col_mode')}</legend>
          {[0, 1, 2].map((m) => (
            <label key={m} style={{ display: 'flex', gap: 8, alignItems: 'center', minHeight: 32 }}>
              <input type="radio" name="pm" checked={mode === m} onChange={() => setMode(m)} />
              {t(`energy:pm.${m}`)}
            </label>
          ))}
        </fieldset>
        <p>{t('energy:pm.dlg_affects', { names: rows.map((r) => r.name).join('、') })}</p>
        {mode === 2 && saving > 0 && <p>{t('energy:pm.dlg_saving', { v: `${fmt.int(saving)}${NBSP}${fmt.unitText}` })}</p>}
        <p className="en-note">{t('energy:pm.dlg_alloc')}</p>
        {mode === 2 && <p className="en-note">{t('energy:pm.dlg_quality')}</p>}
        {err && (
          <p className="en-err" role="alert">
            {err}
          </p>
        )}
        <div className="dialog-actions">
          <button type="button" className="btn" onClick={close}>
            {t('energy:pm.dlg_cancel')}
          </button>
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void run()} data-testid="en-pm-ok">
            {t('energy:pm.dlg_ok', { n: rows.length })}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
